import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getModelsInUse, ModelsInUse } from '../../services/ModelsInUse';
import ClaudeRouteCard from './ClaudeRouteCard';

const price = (n: number | null) => (n == null ? '—' : `$${n.toFixed(2)}`);

const PLATFORM: Record<string, string> = {
  anthropic: "Anthropic API",
  azure: 'Azure OpenAI (Sweden Central)',
  openai: 'OpenAI API',
};

/** 'bedrock:eu-central-1' → 'Bedrock eu-central-1' */
const platformName = (p: string) =>
  p.startsWith('bedrock:') ? `Bedrock ${p.slice('bedrock:'.length)}` : PLATFORM[p] ?? p;

/**
 * Staff: which model each feature runs on, and where. Models are set per
 * feature in backend/src/llm/llm.config.ts; platforms per environment
 * (backend/src/llm/llm.routes.ts). Calls, fallbacks and cost are on the
 * Grafana "AI models" dashboard.
 */
const ModelSettings: React.FC = () => {
  const [data, setData] = useState<ModelsInUse | null>(null);
  const [failed, setFailed] = useState(false);

  const load = () =>
    getModelsInUse().then((res) => {
      if (res.success && res.data) setData(res.data);
      else setFailed(true);
    });

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="w-full space-y-6 p-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Models</h1>
        <p className="text-muted-foreground">
          The model each feature runs on in this environment, and the platforms it is tried on in order: a call moves to
          the next one when a platform is throttled or down. Models are set in{' '}
          <span className="font-mono text-xs">backend/src/llm/llm.config.ts</span>, platforms per environment with{' '}
          <span className="font-mono text-xs">LLM_CLAUDE_ROUTE</span>, or below. Prices are USD per million tokens.
        </p>
      </div>
      <ClaudeRouteCard onChanged={load} />
      <Card className="overflow-hidden">
        {!data ? (
          <div className="flex justify-center py-10 text-sm text-muted-foreground">
            {failed ? 'Could not load the models.' : <Loader2 className="h-5 w-5 animate-spin" />}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Feature</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Runs on</TableHead>
                <TableHead className="text-right">Input</TableHead>
                <TableHead className="text-right">Output</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.features.map((r) => (
                <TableRow key={r.feature}>
                  <TableCell>
                    <div className="font-medium">{r.purpose}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {r.feature} · {r.area}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">{r.model}</span>
                    {r.overridden && <span className="ml-2 text-xs text-amber-600">override</span>}
                    {r.reasoningEffort && (
                      <span className="ml-2 text-xs text-muted-foreground">reasoning: {r.reasoningEffort}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.platforms.map((p, i) => (
                      <div key={p} className={i === 0 ? '' : 'text-xs text-muted-foreground'}>
                        {i === 0 ? platformName(p) : `then ${platformName(p)}`}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{price(r.inputPerMillion)}</TableCell>
                  <TableCell className="text-right tabular-nums">{price(r.outputPerMillion)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      {data && data.otherServices.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Other usage</h2>
          <p className="text-sm text-muted-foreground">
            Service names in the usage data that are not features above: calls made with other clients, or older names.
          </p>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead>How it is called</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.otherServices.map((s) => (
                  <TableRow key={s.service}>
                    <TableCell>
                      <div className="font-medium">{s.purpose}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {s.service} · {s.area}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{s.client}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ModelSettings;
